/**
 * Care Check-in Preferences Widget
 *
 * Lets the user configure mood reminder and check-in frequency preferences.
 * Used in the Care Assessment CoPilot Guide flow.
 */

import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent, cn } from '@tonnie/ui';
import { useCallback } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

interface PreferencesConfig {
  title?: string;
  description?: string;
  variableName?: string;
}

const FREQUENCY_OPTIONS = [
  { id: 'daily', labelKey: 'care.preferences.daily', description: 'care.preferences.dailyDesc' },
  {
    id: 'weekdays',
    labelKey: 'care.preferences.weekdays',
    description: 'care.preferences.weekdaysDesc',
  },
  { id: 'weekly', labelKey: 'care.preferences.weekly', description: 'care.preferences.weeklyDesc' },
] as const;

const TIME_OPTIONS = [
  { id: 'morning', labelKey: 'care.preferences.morning', emoji: '\u2600\ufe0f' },
  { id: 'afternoon', labelKey: 'care.preferences.afternoon', emoji: '\ud83c\udf24\ufe0f' },
  { id: 'evening', labelKey: 'care.preferences.evening', emoji: '\ud83c\udf19' },
] as const;

type PreferencesWidgetProps = WidgetProps & { config?: PreferencesConfig };

export function CareCheckinPreferencesWidget({ config, onComplete }: PreferencesWidgetProps) {
  const { t } = useTranslation();
  // Persist preference selections — small form but the user's actual
  // configuration. Sensible defaults so the form is non-empty on first
  // render even before useWidgetState rehydrates.
  const [persisted, patchPersisted] = useWidgetState<{
    moodReminder: boolean;
    frequency: string;
    preferredTime: string;
  }>('care_checkin_preferences', {
    moodReminder: true,
    frequency: 'daily',
    preferredTime: 'morning',
  });
  const { moodReminder, frequency, preferredTime } = persisted;
  const setMoodReminder = useCallback(
    (v: boolean) => patchPersisted({ moodReminder: v }),
    [patchPersisted]
  );
  const setFrequency = useCallback(
    (v: string) => patchPersisted({ frequency: v }),
    [patchPersisted]
  );
  const setPreferredTime = useCallback(
    (v: string) => patchPersisted({ preferredTime: v }),
    [patchPersisted]
  );

  const handleSubmit = useCallback(() => {
    const varName = config?.variableName ?? 'checkinPreferences';
    onComplete?.({
      [varName]: {
        moodReminder,
        frequency,
        preferredTime,
      },
    });
  }, [moodReminder, frequency, preferredTime, config, onComplete]);

  return (
    <Card className="border-tone-rose-line" data-testid="widget-care_checkin_preferences">
      <CardContent className="pt-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-1">
            {config?.title ?? t('care.preferences.title')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {config?.description ?? t('care.preferences.desc')}
          </p>
        </div>

        {/* Mood reminder toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg border border-border">
          <div>
            <p className="text-sm font-medium">{t('care.preferences.moodReminder')}</p>
            <p className="text-xs text-muted-foreground">
              {t('care.preferences.moodReminderDesc')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={moodReminder}
            aria-label={t('care.preferences.moodReminder')}
            onClick={() => setMoodReminder(!moodReminder)}
            className={cn(
              'relative w-11 h-6 rounded-full transition-colors',
              moodReminder ? 'bg-tone-rose-solid' : 'bg-muted'
            )}
          >
            <div
              className={cn(
                'absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
                moodReminder ? 'translate-x-[22px]' : 'translate-x-0.5'
              )}
            />
          </button>
        </div>

        {/* Frequency */}
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('care.preferences.frequencyLabel')}</label>
          <div className="grid grid-cols-3 gap-2">
            {FREQUENCY_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setFrequency(opt.id)}
                data-testid={`widget-care_checkin_preferences-frequency-${opt.id}`}
                className={cn(
                  'p-3 rounded-lg border-2 text-center transition-all',
                  frequency === opt.id
                    ? 'border-tone-rose-solid bg-tone-rose-subtle'
                    : 'border-border hover:border-tone-rose-line'
                )}
              >
                <p className="text-sm font-medium">{t(opt.labelKey)}</p>
                <p className="text-2xs text-muted-foreground mt-0.5">{t(opt.description, '')}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Preferred time — only meaningful with the reminder on */}
        {moodReminder && (
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('care.preferences.timeLabel')}</label>
            <div className="grid grid-cols-3 gap-2">
              {TIME_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setPreferredTime(opt.id)}
                  className={cn(
                    'p-3 rounded-lg border-2 text-center transition-all',
                    preferredTime === opt.id
                      ? 'border-tone-rose-solid bg-tone-rose-subtle'
                      : 'border-border hover:border-tone-rose-line'
                  )}
                >
                  <span className="text-xl">{opt.emoji}</span>
                  <p className="text-xs font-medium mt-1">{t(opt.labelKey)}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={handleSubmit}
          data-testid="widget-care_checkin_preferences-confirm"
          className="w-full bg-tone-rose-solid hover:bg-tone-rose-solid/90 text-tone-rose-on-solid"
        >
          {t('common.continue')}
        </Button>
      </CardContent>
    </Card>
  );
}
